import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Users, Settings2, Trash2, Shield, ShieldOff, Plus, Edit2, X, Leaf, Sprout, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_COLORS = [
  '#10B981','#3B82F6','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316','#6B7280'
];

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCareModal, setShowCareModal] = useState(false);
  const [editCare, setEditCare] = useState(null);
  const [careForm, setCareForm] = useState({ value: '', label: '', color: '#10B981', icon: 'circle' });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const uploadCareTypeImage = async (careTypeId, file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${API}/api/admin/care-types/${careTypeId}/images`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCareTypes(prev => prev.map(ct =>
        ct.id === careTypeId
          ? { ...ct, images: [...(ct.images || []), data.url] }
          : ct
      ));
    } catch (e) {
      console.error('Upload fehlgeschlagen', e);
      setError('Bild-Upload fehlgeschlagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const deleteCareTypeImage = async (careTypeId, imageUrl) => {
    try {
      await axios.delete(`${API}/api/admin/care-types/${careTypeId}/images?image_url=${encodeURIComponent(imageUrl)}`);
      setCareTypes(prev => prev.map(ct =>
        ct.id === careTypeId
          ? { ...ct, images: (ct.images || []).filter(u => u !== imageUrl) }
          : ct
      ));
    } catch (e) {
      console.error('Bild-Loeschen fehlgeschlagen', e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [uRes, ctRes] = await Promise.all([
        axios.get(`${API}/api/admin/users`),
        axios.get(`${API}/api/care-types`),
      ]);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
      setCareTypes(Array.isArray(ctRes.data) ? ctRes.data : []);
    } catch { setUsers([]); setCareTypes([]); }
    setLoading(false);
  };

  // ── Users ────────────────────────────────────────────────────────────────

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!window.confirm(`Rolle zu "${newRole}" ändern?`)) return;
    try {
      await axios.put(`${API}/api/admin/users/${userId}/role`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      flash('Rolle aktualisiert');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Benutzer "${name}" wirklich löschen? Alle Daten gehen verloren!`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      flash('Benutzer gelöscht');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    }
  };

  // ── Care Types ───────────────────────────────────────────────────────────

  const openCreateCare = () => {
    setEditCare(null);
    setCareForm({ value: '', label: '', color: '#10B981', icon: 'circle' });
    setError('');
    setShowCareModal(true);
  };

  const openEditCare = (ct) => {
    setEditCare(ct);
    setCareForm({ value: ct.value, label: ct.label, color: ct.color || '#10B981', icon: ct.icon || 'circle' });
    setError('');
    setShowCareModal(true);
  };

  const handleSaveCare = async (e) => {
    e.preventDefault();
    if (!careForm.value.trim() || !careForm.label.trim()) { setError('Wert und Bezeichnung erforderlich'); return; }
    setSaving(true);
    setError('');
    try {
      if (editCare) {
        const { data } = await axios.put(`${API}/api/admin/care-types/${editCare.id}`, careForm);
        setCareTypes(prev => prev.map(c => c.id === data.id ? data : c));
      } else {
        const { data } = await axios.post(`${API}/api/admin/care-types`, careForm);
        setCareTypes(prev => [...prev, data]);
      }
      setShowCareModal(false);
      flash('Pflegetyp gespeichert');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    }
    setSaving(false);
  };

  const handleDeleteCare = async (ct) => {
    if (ct.is_default) { setError('Standard-Pflegetypen können nicht gelöscht werden'); return; }
    if (!window.confirm(`Pflegetyp "${ct.label}" löschen?`)) return;
    try {
      await axios.delete(`${API}/api/admin/care-types/${ct.id}`);
      setCareTypes(prev => prev.filter(c => c.id !== ct.id));
      flash('Pflegetyp gelöscht');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 animate-fade-in" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
          <Shield size={20} className="text-emerald-400" />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold text-emerald-50">Admin-Panel</h1>
          <p className="text-emerald-100/50 text-sm">Benutzer & Einstellungen verwalten</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex rounded-lg p-1 w-fit" style={{ background: '#0A120D', border: '1px solid #1B2A21' }}>
        {[
          { key: 'users', label: 'Benutzer', icon: Users },
          { key: 'care-types', label: 'Pflegetypen', icon: Settings2 },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === key ? 'bg-emerald-500 text-white' : 'text-emerald-100/50 hover:text-emerald-100'}`}
            data-testid={`admin-tab-${key}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="p-3 rounded-lg text-sm text-emerald-400" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
          {successMsg}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-lg text-sm text-red-400" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-400/60 hover:text-red-400"><X size={12} /></button>
        </div>
      )}

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <div className="space-y-2" data-testid="users-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-emerald-100/50">{users.length} Benutzer(</p>
          </div>
          {users.map(u => (
            <div key={u.id} className="card-grow p-4" data-testid={`user-row-${u.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                  style={{ background: '#111C15', border: '1px solid #1B2A21' }}>
                  {u.avatar ? (
                    <img src={u.avatar} alt={u.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-emerald-400 text-sm font-bold">
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-emerald-100">{u.name}</span>
                    <span className={`badge ${u.role === 'admin' ? 'badge-green' : 'badge-blue'}`}>
                      {u.role === 'admin' ? 'Admin' : 'User'}
                    </span>
                    {u.id === user?.id && <span className="badge badge-yellow">Du</span>}
                  </div>
                  <p className="text-xs text-emerald-100/40">{u.email}</p>
                  <div className="flex gap-3 text-xs text-emerald-100/30 mt-0.5">
                    <span className="flex items-center gap-1"><Sprout size={10} />{u.project_count} Projekte</span>
                    <span className="flex items-center gap-1"><Leaf size={10} />{u.plant_count} Pflanzen</span>
                    <span>Seit {new Date(u.created_at).toLocaleDateString('de-DE')}</span>
                  </div>
                </div>
                {u.id !== user?.id && (
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleRoleChange(u.id, u.role)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${u.role === 'admin' ? 'text-yellow-400 hover:bg-yellow-400/10' : 'text-emerald-400 hover:bg-emerald-400/10'}`}
                      style={{ border: `1px solid ${u.role === 'admin' ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'}` }}
                      data-testid={`role-btn-${u.id}`}>
                      {u.role === 'admin' ? <><ShieldOff size={12} /> User</> : <><Shield size={12} /> Admin</>}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u.id, u.name)}
                      className="p-1.5 rounded-lg text-emerald-100/30 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      data-testid={`delete-user-${u.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── CARE TYPES TAB ── */}
      {tab === 'care-types' && (
        <div className="space-y-3" data-testid="care-types-tab">
          <div className="flex items-center justify-between">
            <p className="text-sm text-emerald-100/50">{careTypes.length} Pflegetypen</p>
            <button onClick={openCreateCare} className="btn-primary flex items-center gap-2 text-sm" data-testid="create-care-type-btn">
              <Plus size={14} /> Neuer Typ
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-3" data-testid="care-types-grid">
            {careTypes.map(ct => (
              <div key={ct.id} className="card-grow p-3" data-testid={`care-type-${ct.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${ct.color}20`, border: `1px solid ${ct.color}40` }}>
                    <span className="w-3 h-3 rounded-full" style={{ background: ct.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-emerald-100">{ct.label}</p>
                    <p className="text-xs text-emerald-100/40 font-mono">{ct.value}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {ct.is_default && <span className="badge badge-green text-xs">Standard</span>}
                    <button onClick={() => openEditCare(ct)} className="p-1.5 rounded text-emerald-100/40 hover:text-emerald-400 transition-colors" data-testid={`edit-care-type-${ct.id}`}>
                      <Edit2 size={12} />
                    </button>
                    {!ct.is_default && (
                      <button onClick={() => handleDeleteCare(ct)} className="p-1.5 rounded text-emerald-100/40 hover:text-red-400 transition-colors" data-testid={`delete-care-type-${ct.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                    <label
                      className={`p-1.5 rounded transition-colors cursor-pointer ${uploadingImage ? 'text-emerald-100/20' : 'text-emerald-100/40 hover:text-emerald-400'}`}
                      title="Bild hochladen"
                      data-testid={`upload-image-${ct.id}`}>
                      <ImageIcon size={12} />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploadingImage}
                        onChange={e => {
                          if (e.target.files[0]) uploadCareTypeImage(ct.id, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {ct.images && ct.images.length > 0 && (
                  <div className="mt-2 flex gap-2 flex-wrap">
                    {ct.images.map((url, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={`${API}${url}`}
                          alt={`${ct.label} Bild ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded-lg"
                          style={{ border: '1px solid #1B2A21' }}
                        />
                        <button
                          onClick={() => deleteCareTypeImage(ct.id, url)}
                          className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: 'rgba(239,68,68,0.9)' }}
                          title="Bild entfernen"
                          data-testid={`delete-image-${ct.id}-${idx}`}>
                          <X size={10} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Care Type Modal */}
      {showCareModal && (
        <div className="modal-backdrop" data-testid="care-type-modal">
          <div className="modal-content p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-emerald-50">{editCare ? 'Pflegetyp bearbeiten' : 'Neuer Pflegetyp'}</h2>
              <button onClick={() => setShowCareModal(false)} className="text-emerald-100/40 hover:text-emerald-100"><X size={20} /></button>
            </div>
            <form onSubmit={handleSaveCare} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Bezeichnung *</label>
                  <input className="input-grow" placeholder="z.B. Flush" value={careForm.label} onChange={e => setCareForm(f => ({ ...f, label: e.target.value }))} data-testid="care-type-label-input" />
                </div>
                <div>
                  <label className="block text-xs text-emerald-100/60 mb-1 uppercase tracking-wider">Wert (intern) *</label>
                  <input className="input-grow font-mono text-sm" placeholder="z.B. flush" value={careForm.value} onChange={e => setCareForm(f => ({ ...f, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} data-testid="care-type-value-input" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-emerald-100/60 mb-1.5 uppercase tracking-wider">Farbe</label>
                <div className="flex gap-2 flex-wrap">
                  {ICON_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setCareForm(f => ({ ...f, color: c }))}
                      className={`w-7 h-7 rounded-full transition-all ${careForm.color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#111C15] scale-110' : ''}`}
                      style={{ background: c }} />
                  ))}
                  <input type="color" className="w-7 h-7 rounded cursor-pointer" value={careForm.color} onChange={e => setCareForm(f => ({ ...f, color: e.target.value }))} />
                </div>
              </div>
              {error && <p className="text-red-400 text-sm">{error}</p>}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCareModal(false)} className="btn-secondary flex-1">Abbrechen</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1" data-testid="save-care-type-btn">
                  {saving ? 'Speichern...' : editCare ? 'Aktualisieren' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
